<template>
  <IntercomLayout>
    <template #topbar>
      <IntercomTopbar
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="category-detail-view">
      <!-- Back link -->
      <button class="back-link" @click="router.push('/categories')">
        &larr; Back to Categories
      </button>

      <div v-if="isLoading" class="loading-state">Loading...</div>

      <div v-else-if="category" class="category-content">
        <!-- Header -->
        <div class="page-header">
          <div>
            <h1 class="page-title">{{ category.name }}</h1>
            <p v-if="category.description" class="page-subtitle">{{ category.description }}</p>
          </div>
          <div class="header-actions">
            <IntercomButton variant="secondary" @click="handleEditCategory">
              Edit Category
            </IntercomButton>
          </div>
        </div>

        <!-- Summary Stats -->
        <div class="summary-grid">
          <div class="summary-card">
            <span class="summary-label">Products</span>
            <span class="summary-value">{{ summary.productCount }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Total Stock</span>
            <span class="summary-value">{{ formatNumber(summary.totalStockQty) }} units</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Stock Value</span>
            <span class="summary-value">{{ formatCurrency(summary.totalStockValue) }}</span>
          </div>
          <div class="summary-card">
            <span class="summary-label">Default HSN</span>
            <span class="summary-value">{{ category.default_hsn_code || '-' }}</span>
          </div>
        </div>

        <!-- Products Table -->
        <IntercomCard>
          <div class="section-header">
            <h3 class="section-heading">Products</h3>
            <IntercomButton variant="primary" size="sm" @click="handleAddProduct">
              + Add Product
            </IntercomButton>
          </div>

          <!-- Search within category -->
          <div class="search-bar">
            <div class="search-input-wrapper">
              <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                class="search-input"
                placeholder="Search products in this category..."
                @input="handleSearchInput"
              />
              <button v-if="searchQuery" class="search-clear" @click="clearSearch">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <IntercomTable
            :columns="productColumns"
            :data="products"
            :loading="productsLoading"
            hoverable
            clickable
            @row-click="handleEditProduct"
          >
            <template #cell-base_price="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-cost_price="{ value }">
              {{ value ? formatCurrency(value) : '-' }}
            </template>
            <template #cell-stock_quantity="{ row, value }">
              <span :class="{ 'low-stock': isLowStock(row) }">
                {{ formatNumber(value) }}
              </span>
              <IntercomBadge v-if="isLowStock(row)" variant="warning" class="low-stock-badge">
                Low
              </IntercomBadge>
            </template>
            <template #cell-cgst_rate="{ row }">
              {{ row.cgst_rate + row.sgst_rate }}%
            </template>
            <template #cell-unit_type="{ value }">
              {{ value || '-' }}
            </template>
            <template #cell-actions="{ row }">
              <IntercomButton size="sm" variant="ghost" @click.stop="handleEditProduct(row)">
                Edit
              </IntercomButton>
            </template>
          </IntercomTable>
        </IntercomCard>

        <IntercomPagination
          :offset="offset"
          :total="productsTotal"
          :limit="productsLimit"
          @update:offset="(v) => { offset = v; fetchProducts() }"
        />
      </div>

      <div v-else class="empty-state">Category not found</div>

      <!-- Edit Category Modal -->
      <CategoryForm
        v-model:show="showCategoryModal"
        :category="category"
        @success="handleCategoryFormSuccess"
      />

      <!-- Product Form Modal -->
      <ProductForm
        v-model:show="showProductModal"
        :product="selectedProduct"
        @success="handleProductFormSuccess"
      />
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomCard,
  IntercomTable,
  IntercomButton,
  IntercomBadge,
  IntercomPagination,
} from '@shared/components/intercom'
import CategoryForm from '../components/CategoryForm.vue'
import ProductForm from '../components/ProductForm.vue'
import { billingNavSections } from '../config/navigation.js'

const route = useRoute()
const router = useRouter()
const { user, logout } = useAuth()
const { getCategoryDetail, listProducts, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections
const categoryId = route.params.id

// State
const category = ref(null)
const summary = ref({ productCount: 0, totalStockQty: 0, totalStockValue: 0 })
const isLoading = ref(true)
const showCategoryModal = ref(false)
const showProductModal = ref(false)
const selectedProduct = ref(null)
const searchQuery = ref('')

// Products paginated list
const {
  items: products,
  total: productsTotal,
  loading: productsLoading,
  offset,
  limit: productsLimit,
  fetch: fetchProducts,
  resetAndFetch,
} = usePaginatedList((params) =>
  listProducts({ ...params, category_id: categoryId })
)

// Product table columns
const productColumns = [
  { key: 'name', title: 'Name', width: '200px' },
  { key: 'sku', title: 'SKU', width: '120px' },
  { key: 'base_price', title: 'Price', align: 'right', width: '120px' },
  { key: 'cost_price', title: 'Cost Price', align: 'right', width: '120px' },
  { key: 'stock_quantity', title: 'Stock Qty', align: 'right', width: '120px' },
  { key: 'cgst_rate', title: 'GST %', align: 'center', width: '80px' },
  { key: 'unit_type', title: 'Unit', width: '80px' },
  { key: 'actions', title: '', width: '80px', align: 'right' },
]

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0)

const formatNumber = (val) =>
  parseFloat(Number(val || 0).toFixed(3))

const isLowStock = (product) =>
  product.min_stock_quantity > 0 && product.stock_quantity <= product.min_stock_quantity

const fetchCategoryDetail = async () => {
  isLoading.value = true
  try {
    const response = await getCategoryDetail(categoryId)
    category.value = response.category || response
    summary.value = {
      productCount: response.product_count || 0,
      totalStockQty: response.total_stock_qty || 0,
      totalStockValue: response.total_stock_value || 0,
    }
  } catch (error) {
    console.error('Failed to fetch category detail:', error)
    category.value = null
  } finally {
    isLoading.value = false
  }
}

const handleEditCategory = () => {
  showCategoryModal.value = true
}

const handleCategoryFormSuccess = async () => {
  showCategoryModal.value = false
  await fetchCategoryDetail()
}

const handleAddProduct = () => {
  selectedProduct.value = { category_id: parseInt(categoryId) }
  showProductModal.value = true
}

const handleEditProduct = (product) => {
  selectedProduct.value = product
  showProductModal.value = true
}

const handleProductFormSuccess = async () => {
  showProductModal.value = false
  selectedProduct.value = null
  await fetchCategoryDetail()
  await fetchProducts()
}

const handleSearchInput = () => {
  resetAndFetch({ search: searchQuery.value })
}

const clearSearch = () => {
  searchQuery.value = ''
  resetAndFetch({ search: '' })
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
  fetchCategoryDetail()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.category-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) 0;
  margin-bottom: var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  background: none;
  border: none;
  cursor: pointer;
}

.back-link:hover {
  text-decoration: underline;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: var(--space-1) 0 0;
}

.header-actions {
  display: flex;
  gap: var(--space-2);
}

/* Summary Stats */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.summary-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-4);
  background: var(--color-white);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
}

.summary-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

/* Section header */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
}

.section-heading {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Search Bar */
.search-bar {
  display: flex;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
}

.search-input-wrapper {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  color: var(--color-gray-400);
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 0 var(--space-10) 0 44px;
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  outline: none;
  transition: all var(--transition-fast);
}

.search-input:focus {
  border-color: var(--color-blue);
  box-shadow: 0 0 0 3px rgba(0, 87, 255, 0.1);
  background: var(--color-white);
}

.search-clear {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--color-gray-100);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: var(--color-gray-500);
  transition: all var(--transition-fast);
}

.search-clear:hover {
  background: var(--color-gray-200);
  color: var(--color-gray-700);
}

/* Low stock */
.low-stock {
  color: #dc2626;
  font-weight: var(--font-weight-semibold);
}

.low-stock-badge {
  margin-left: var(--space-1);
}

/* Loading & Empty states */
.loading-state {
  padding: var(--space-16);
  text-align: center;
  color: var(--color-gray-500);
}

.empty-state {
  padding: var(--space-16);
  text-align: center;
  color: var(--color-gray-500);
  font-size: var(--font-size-lg);
}

@media (max-width: 768px) {
  .category-detail-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    gap: var(--space-4);
  }

  .summary-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
  }
}
</style>
